import React, { useEffect, useState, useRef } from 'react';
import EnterpriseProvider from '../../infra/provider/enterpriseProvider';
import UploadImageProvider from '../../infra/provider/uploadImageProvider';
import Enterprise from '../../domain/entities/enterprise';
import ConstructionStep from '../../domain/entities/constructionStep';
import Step from '../../domain/entities/step';
import Tooltip from '../tootip/Tootip';
import ConstructionStatus from '../../domain/entities/valueObjects/constructionStatus';
import '../../styles/components/_constructionsteps.scss';
import { Hammer, Plus, MapPinHouse, X, Upload, BarChart3, Layers, LucideImage, History, Clock, Camera, CheckCircle2, AlertCircle, Search, Filter, Edit, Trash2, Calendar, Eye, User, ArrowRight, HardHat, ChartNoAxesColumn, Wrench, Drill, Pickaxe, DotIcon, EllipsisVertical } from 'lucide-react';
import ConstructionStepNew from '../../domain/entities/constructionStepNew';
import { toast, ToastContainer } from 'react-toastify';

const icons = [
    <Hammer className="icon" />,
    <Wrench className="icon" />,
    <Drill className="icon" />,
    <HardHat className="icon" />,
    <Pickaxe className="icon" />,
    // ...adicione outros
];

const getRandomIcon = () => {
    const idx = Math.floor(Math.random() * icons.length);
    return icons[idx];
};

const getIconById = (id: string) => {
    const idx = parseInt(id) % icons.length;
    return icons[idx];
};

// Função auxiliar para formatar data corretamente
const formatDate = (timestamp: number | string | null | undefined): string => {
    if (!timestamp) return "Data não definida";

    try {
        let date: Date;

        // Se for string, verificar se é uma string ISO ou timestamp
        if (typeof timestamp === 'string') {
            // Se parece ser uma string ISO (contém 'T' ou é uma data válida)
            if (timestamp.includes('T') || timestamp.includes('-')) {
                date = new Date(timestamp);
            } else {
                // Tentar converter para número (timestamp Unix)
                const numTimestamp = parseInt(timestamp);
                if (isNaN(numTimestamp)) {
                    return "Data não definida";
                }
                // Converter de segundos para milissegundos
                date = new Date(numTimestamp * 1000);
            }
        } else {
            // Se for número, assumir que está em segundos
            date = new Date(timestamp * 1000);
        }

        // Verificar se a data é válida
        if (isNaN(date.getTime())) {
            return "Data não definida";
        }

        // Formatar a data no padrão brasileiro DD/MM/AAAA
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();

        return `${day}/${month}/${year}`;
    } catch (error) {
        console.error('Erro ao formatar data:', error);
        return "Data não definida";
    }
};

const ConstructionSteps = () => {
    const [enterprises, setEnterprises] = useState<Enterprise[]>([]);
    const [selectedEnterprise, setSelectedEnterprise] = useState('');
    const [loading, setLoading] = useState(true);
    const [steps, setSteps] = useState<ConstructionStep[] | null>(null);
    const [stepsLoading, setStepsLoading] = useState(false);
    const [showCreateStepModal, setShowCreateStepModal] = useState(false);
    const [isNewStageDialogOpen, setIsNewStageDialogOpen] = useState(false);

    // Estados para a aba Etapas
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [sortBy, setSortBy] = useState('name');
    const [selectedStage, setSelectedStage] = useState<string | null>(null);
    const [editingStage, setEditingStage] = useState<ConstructionStep | null>(null);
    const [isDialogOpen, setIsDialogOpen] = useState(false);

    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

    // Estados para os campos do formulário de etapa
    const [stepName, setStepName] = useState('');
    const [stepProgress, setStepProgress] = useState(0);
    const [stepStatus, setStepStatus] = useState('pending');
    const [stepStartDate, setStepStartDate] = useState('');
    const [stepEndDate, setStepEndDate] = useState('');
    const [stepObs, setStepObs] = useState('');
    const [stepImages, setStepImages] = useState<File[]>([]);
    const [isUpdating, setIsUpdating] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isCreating, setIsCreating] = useState(false);

    // Estados para modal de confirmação de exclusão de imagem
    const [deleteImageModalOpen, setDeleteImageModalOpen] = useState(false);
    const [imageToDelete, setImageToDelete] = useState<{ stageId: string; imageId: string } | null>(null);
    const [isDeletingImage, setIsDeletingImage] = useState(false);

    const enterpriseProvider = EnterpriseProvider.getInstance();

    // Funções para filtros e ordenação
    const getFilteredAndSortedStages = () => {
        if (!steps) return [];

        let filtered = steps.filter(stage => {
            const matchesSearch = stage.name.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesStatus = statusFilter === 'all' ||
                (statusFilter === 'completed' && stage.status?.getStatus?.() === 'finished') ||
                (statusFilter === 'in-progress' && stage.status?.getStatus?.() === 'in_progress') ||
                (statusFilter === 'not-started' && stage.status?.getStatus?.() === 'pending');

            return matchesSearch && matchesStatus;
        });

        // Ordenação
        filtered.sort((a, b) => {
            switch (sortBy) {
                case 'name':
                    return a.name.localeCompare(b.name);
                case 'progress':
                    return ((b as any).progress || 0) - ((a as any).progress || 0);
                case 'date':
                    const dateA = (a as any).startDate ? new Date((a as any).startDate * 1000).getTime() : 0;
                    const dateB = (b as any).startDate ? new Date((b as any).startDate * 1000).getTime() : 0;
                    return dateB - dateA;
                default:
                    return 0;
            }
        });

        return filtered;
    };

    const getStatusText = (status: ConstructionStatus) => {
        const statusValue = status?.getStatus?.();
        if (statusValue === 'finished') return 'Concluída';
        if (statusValue === 'in_progress') return 'Em Andamento';
        return 'Não Iniciada';
    };

    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [stageIdToDelete, setStageIdToDelete] = useState<string | null>(null);

    const handleDeleteStage = (stageId: string) => {
        setStageIdToDelete(stageId);
        setDeleteModalOpen(true);
    };

    const confirmDeleteStage = async () => {
        if (stageIdToDelete && !isDeleting) {
            setIsDeleting(true);

            try {
                // Chamar o método de exclusão do provider
                await enterpriseProvider.deleteEnterpriseConstructionStepNew(stageIdToDelete);

                // Recarregar os dados do backend para garantir sincronização
                try {
                    const result = await enterpriseProvider.getEnterpriseConstructionStepNew(selectedEnterprise);
                    // Converter ConstructionStepNew[] para ConstructionStep[]
                    const convertedSteps = result.map((step, index) => {
                        const constructionStep = new ConstructionStep(
                            step.id?.toString() || `step-${index}`, // id
                            step.name || '', // name
                            step.comment || '', // description
                            0, // estimationDuration
                            '', // durationType
                            new ConstructionStatus(step.status), // status
                            [] as Step[], // steps
                            step.action || '' // action
                        );

                        // Adicionar campos extras para compatibilidade com o componente
                        (constructionStep as any).progress = step.progress;
                        (constructionStep as any).startDate = step.startDate;
                        (constructionStep as any).endDate = step.endDate;
                        (constructionStep as any).observations = step.comment;

                        // Mapear imagens se existirem no resultado da API
                        if ((step as any).images && Array.isArray((step as any).images)) {
                            (constructionStep as any).images = (step as any).images;
                        } else {
                            (constructionStep as any).images = [];
                        }

                        // Mapear histórico se existir no resultado da API
                        if ((step as any).histories && Array.isArray((step as any).histories)) {
                            (constructionStep as any).history = (step as any).histories.map((historyItem: any) => {
                                // Determinar o tipo de ação baseado no título ou ação
                                let actionType = historyItem.action || 'updated';
                                let description = historyItem.title || 'Etapa atualizada';

                                // Melhorar a detecção do tipo de ação
                                if (historyItem.action === 'created') {
                                    actionType = 'created';
                                    description = 'Etapa criada';
                                } else if (historyItem.title?.toLowerCase().includes('status')) {
                                    actionType = 'status_changed';
                                    description = historyItem.title || 'Status da etapa alterado';
                                } else if (historyItem.title?.toLowerCase().includes('progresso') || historyItem.title?.toLowerCase().includes('progress')) {
                                    actionType = 'progress_updated';
                                    description = historyItem.title || 'Progresso da etapa atualizado';
                                } else if (historyItem.title?.toLowerCase().includes('imagem') || historyItem.title?.toLowerCase().includes('foto')) {
                                    if (historyItem.title?.toLowerCase().includes('removida') || historyItem.title?.toLowerCase().includes('excluída')) {
                                        actionType = 'image_removed';
                                        description = historyItem.title || 'Imagem removida da etapa';
                                    } else {
                                        actionType = 'image_added';
                                        description = historyItem.title || 'Imagem adicionada à etapa';
                                    }
                                } else if (historyItem.title?.toLowerCase().includes('data') || historyItem.title?.toLowerCase().includes('data')) {
                                    actionType = 'date_updated';
                                    description = historyItem.title || 'Data da etapa atualizada';
                                }

                                return {
                                    id: historyItem.id?.toString() || Date.now().toString(),
                                    action: actionType,
                                    description: description,
                                    timestamp: historyItem.createdAt ? new Date(historyItem.createdAt * 1000).toISOString() : new Date().toISOString(),
                                    user: historyItem.user?.email || 'Usuário',
                                    changes: []
                                };
                            });
                        } else {
                            (constructionStep as any).history = [];
                        }

                        return constructionStep;
                    });
                    setSteps(convertedSteps);
                } catch (error) {
                    console.error('Erro ao recarregar etapas após exclusão:', error);
                    // Em caso de erro, ainda remove localmente para não perder a exclusão
                    setSteps(prev => prev ? prev.filter(s => s.id !== stageIdToDelete) : []);
                }

                // Fechar modal
                setDeleteModalOpen(false);
                setStageIdToDelete(null);
                toast.success('Etapa excluída com sucesso!');

            } catch (error) {
                console.error('Erro ao excluir etapa:', error);
                toast.error('Erro ao excluir etapa. Tente novamente.');
            } finally {
                setIsDeleting(false);
            }
        }
    };

    const cancelDeleteStage = () => {
        setDeleteModalOpen(false);
        setStageIdToDelete(null);
    };

    // Funções para o histórico
    const getActionColor = (action: string) => {
        switch (action) {
            case 'create':
            case 'created': return 'action-created';
            case 'update':
            case 'updated': return 'action-updated';
            case 'deleted': return 'action-deleted';
            case 'status_changed': return 'action-status';
            case 'progress_updated': return 'action-progress';
            case 'image_added': return 'action-image';
            case 'image_removed': return 'action-image-removed';
            case 'date_updated': return 'action-date';
            default: return 'action-updated';
        }
    };

    const getActionIcon = (action: string) => {
        switch (action) {
            case 'created': return <Plus className="w-4 h-4 text-green-600" />;
            case 'updated': return <Edit className="w-4 h-4 text-blue-600" />;
            case 'deleted': return <Trash2 className="w-4 h-4 text-red-600" />;
            case 'status_changed': return <CheckCircle2 className="w-4 h-4 text-purple-600" />;
            case 'progress_updated': return <BarChart3 className="w-4 h-4 text-orange-600" />;
            case 'image_added': return <Camera className="w-4 h-4 text-teal-600" />;
            case 'image_removed': return <X className="w-4 h-4 text-red-500" />;
            case 'date_updated': return <Calendar className="w-4 h-4 text-indigo-600" />;
            default: return <Edit className="w-4 h-4 text-blue-600" />;
        }
    };

    const getActionText = (action: string) => {
        switch (action) {
            case 'created': return 'Criação';
            case 'updated': return 'Edição';
            case 'deleted': return 'Exclusão';
            case 'status_changed': return 'Status Alterado';
            case 'progress_updated': return 'Progresso Atualizado';
            case 'image_added': return 'Imagem Adicionada';
            case 'image_removed': return 'Imagem Removida';
            case 'date_updated': return 'Data Atualizada';
            default: return 'Alteração';
        }
    };

    const filteredStages = getFilteredAndSortedStages();
    const stages = steps || [];

    const handleEnterpriseChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setSelectedEnterprise(e.target.value);
    };

    const handleUploadBoxClick = () => {
        if (fileInputRef.current) fileInputRef.current.click();
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            setSelectedFiles(Array.from(e.target.files));
        }
    };

    const clearFormFields = () => {
        setStepName('');
        setStepProgress(0);
        setStepStatus('pending');
        setStepStartDate('');
        setStepEndDate('');
        setStepObs('');
        setStepImages([]);
        setSelectedFiles([]);
        // Limpar o input de arquivo
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleOpenCreateModal = () => {
        clearFormFields();
        setShowCreateStepModal(true);
    };

    const handleCreateStep = async (e: React.FormEvent) => {
        e.preventDefault();

        if (isCreating) return; // Prevenir múltiplos submits
        setIsCreating(true);

        try {
            // 1. Fazer upload das imagens primeiro
            const uploadImageProvider = UploadImageProvider.getInstance();
            const uploadedImageUrls: string[] = [];

            if (stepImages.length > 0) {
                for (const image of stepImages) {
                    try {
                        const imageUrl = await uploadImageProvider.upload(image);
                        uploadedImageUrls.push(imageUrl);
                    } catch (error) {
                        console.error('Erro ao fazer upload da imagem:', error);
                        toast.error('Erro ao fazer upload de uma das imagens. Tente novamente.');
                        return;
                    }
                }
            }

            // 2. Criar o step
            const newStep = new ConstructionStep(
                Date.now().toString(), // id
                stepName, // name
                stepObs, // description
                0, // estimationDuration
                '', // durationType
                new ConstructionStatus(stepStatus), // status
                [] as Step[] // steps
            );

            // Converter stepStartDate e stepEndDate para timestamp em segundos (não milissegundos)
            const startDateTimestamp = stepStartDate ? Math.floor(new Date(stepStartDate).getTime() / 1000).toString() : '';
            const endDateTimestamp = stepEndDate ? Math.floor(new Date(stepEndDate).getTime() / 1000).toString() : '';

            const result = await enterpriseProvider.createEnterpriseConstructionStepNew(selectedEnterprise, new ConstructionStepNew(
                undefined, // id
                undefined, // name
                stepName, // title
                stepStatus, // status
                stepObs, // comment
                stepProgress, // progress
                startDateTimestamp || undefined, // startDate
                endDateTimestamp || null // endDate
            ));

            // 3. Adicionar as imagens ao step criado
            if (uploadedImageUrls.length > 0 && result && result.id) {
                for (const imageUrl of uploadedImageUrls) {
                    try {
                        await enterpriseProvider.createEnterpriseConstructionStepImageNew(
                            result.id.toString(),
                            imageUrl
                        );
                    } catch (error) {
                        console.error('Erro ao adicionar imagem ao step:', error);
                        // Não interromper o processo se falhar ao adicionar imagem
                    }
                }
            }

            // Adiciona campos extras para o dashboard
            (newStep as any).progress = Number(stepProgress);
            (newStep as any).startDate = stepStartDate;
            (newStep as any).endDate = stepEndDate;
            (newStep as any).images = uploadedImageUrls.map((url, index) => ({
                url,
                description: stepImages[index]?.name || `Imagem ${index + 1}`
            }));
            // 4. Recarregar os dados do backend para garantir sincronização
            try {
                const result = await enterpriseProvider.getEnterpriseConstructionStepNew(selectedEnterprise);
                // Converter ConstructionStepNew[] para ConstructionStep[]
                const convertedSteps = result.map((step, index) => {
                    const constructionStep = new ConstructionStep(
                        step.id?.toString() || `step-${index}`, // id
                        step.name || '', // name
                        step.comment || '', // description
                        0, // estimationDuration
                        '', // durationType
                        new ConstructionStatus(step.status), // status
                        [] as Step[] // steps
                    );

                    // Adicionar campos extras para compatibilidade com o componente
                    (constructionStep as any).progress = step.progress;
                    (constructionStep as any).startDate = step.startDate;
                    (constructionStep as any).endDate = step.endDate;
                    (constructionStep as any).observations = step.comment;

                    // Mapear imagens se existirem no resultado da API
                    if ((step as any).images && Array.isArray((step as any).images)) {
                        (constructionStep as any).images = (step as any).images;
                    } else {
                        (constructionStep as any).images = [];
                    }

                    // Mapear histórico se existir no resultado da API
                    if ((step as any).histories && Array.isArray((step as any).histories)) {
                        (constructionStep as any).history = (step as any).histories.map((historyItem: any) => {
                            // Determinar o tipo de ação baseado no título ou ação
                            let actionType = historyItem.action || 'updated';
                            let description = historyItem.title || 'Etapa atualizada';

                            // Melhorar a detecção do tipo de ação
                            if (historyItem.action === 'created') {
                                actionType = 'created';
                                description = 'Etapa criada';
                            } else if (historyItem.title?.toLowerCase().includes('status')) {
                                actionType = 'status_changed';
                                description = historyItem.title || 'Status da etapa alterado';
                            } else if (historyItem.title?.toLowerCase().includes('progresso') || historyItem.title?.toLowerCase().includes('progress')) {
                                actionType = 'progress_updated';
                                description = historyItem.title || 'Progresso da etapa atualizado';
                            } else if (historyItem.title?.toLowerCase().includes('imagem') || historyItem.title?.toLowerCase().includes('foto')) {
                                if (historyItem.title?.toLowerCase().includes('removida') || historyItem.title?.toLowerCase().includes('excluída')) {
                                    actionType = 'image_removed';
                                    description = historyItem.title || 'Imagem removida da etapa';
                                } else {
                                    actionType = 'image_added';
                                    description = historyItem.title || 'Imagem adicionada à etapa';
                                }
                            } else if (historyItem.title?.toLowerCase().includes('data') || historyItem.title?.toLowerCase().includes('data')) {
                                actionType = 'date_updated';
                                description = historyItem.title || 'Data da etapa atualizada';
                            }

                            return {
                                id: historyItem.id?.toString() || Date.now().toString(),
                                action: actionType,
                                description: description,
                                timestamp: historyItem.createdAt ? new Date(historyItem.createdAt * 1000).toISOString() : new Date().toISOString(),
                                user: historyItem.user?.email || 'Usuário',
                                changes: []
                            };
                        });
                    } else {
                        (constructionStep as any).history = [];
                    }

                    return constructionStep;
                });
                setSteps(convertedSteps);
            } catch (error) {
                console.error('Erro ao recarregar etapas após criação:', error);
                // Em caso de erro, ainda adiciona a etapa localmente para não perder a criação
                (newStep as any).history = [{
                    id: Date.now().toString(),
                    action: 'created',
                    description: 'Etapa criada',
                    timestamp: new Date().toISOString(),
                    user: 'Usuário atual',
                    changes: [],
                }];
                setSteps(prev => prev ? [...prev, newStep] : [newStep]);
            }

            setShowCreateStepModal(false);
            setIsNewStageDialogOpen(false); // Fechar o modal de nova etapa
            clearFormFields();
            toast.success('Etapa criada com sucesso!');
        } catch (error) {
            console.error('Erro ao criar etapa:', error);
            toast.error('Erro ao criar etapa. Tente novamente.');
        } finally {
            setIsCreating(false);
        }
    };

    const getEtapasByStatus = (steps: ConstructionStep[]) => {
        return {
            concluidas: steps.filter(s => s.status?.getStatus?.() === 'finished'),
            andamento: steps.filter(s => s.status?.getStatus?.() === 'in_progress'),
            naoIniciadas: steps.filter(s => s.status?.getStatus?.() === 'pending'),
        };
    };
    const getProgressoGeral = (steps: ConstructionStep[]) => {
        if (!steps || steps.length === 0) return 0;
        const total = steps.reduce((acc, s) => acc + ((s as any).progress || 0), 0);
        return Math.round(total / steps.length);
    };
    const getTotalFotos = (steps: ConstructionStep[]) => {
        if (!steps) return 0;
        return steps.reduce((acc, s) => acc + (((s as any).images?.length) || 0), 0);
    };

    const getStatusIcon = (status: ConstructionStatus) => {
        const statusValue = status?.getStatus?.();
        if (statusValue === 'finished') return <ChartNoAxesColumn className="icon green" />;
        if (statusValue === 'in_progress') return <Layers className="icon blue" />;
        return <Clock className="icon gray" />;
    };

    const handleViewHistory = (stage: ConstructionStep) => {
        setActiveTab('historico');
    };

    const handleShowDetails = (stage: ConstructionStep) => {
        setSelectedStage(stage.id);
    };

    const handleDeleteImage = (stageId: string, imageId: string) => {
        setImageToDelete({ stageId, imageId });
        setDeleteImageModalOpen(true);
    };

    const confirmDeleteImage = async () => {
        if (!imageToDelete) return;

        setIsDeletingImage(true);
        try {
            await enterpriseProvider.deleteEnterpriseConstructionStepImageNew(imageToDelete.stageId, imageToDelete.imageId);

            // Atualizar o estado local removendo a imagem
            setSteps(prev => prev ? prev.map(s => {
                if (s.id === imageToDelete.stageId) {
                    const updatedImages = (s as any).images?.filter((img: any) => img.id !== imageToDelete.imageId) || [];
                    return {
                        ...s,
                        images: updatedImages
                    };
                }
                return s;
            }) : []);

            toast.success('Imagem excluída com sucesso!');
            setDeleteImageModalOpen(false);
            setImageToDelete(null);
        } catch (error) {
            console.error('Erro ao excluir imagem:', error);
            toast.error('Erro ao excluir imagem. Tente novamente.');
        } finally {
            setIsDeletingImage(false);
        }
    };

    const cancelDeleteImage = () => {
        setDeleteImageModalOpen(false);
        setImageToDelete(null);
    };

    const [activeTab, setActiveTab] = useState('visao');

    useEffect(() => {
        const fetchEnterprises = async () => {
            setLoading(true);
            const enterpriseProvider = EnterpriseProvider.getInstance();
            try {
                const enterprisesList = await enterpriseProvider.getEnterprisesName();
                console.log('Empreendimentos retornados:', enterprisesList);
                setEnterprises(enterprisesList);
            } catch (error) {
                setEnterprises([]);
            }
            setLoading(false);
        };
        fetchEnterprises();
    }, []);

    useEffect(() => {
        if (!selectedEnterprise) {
            setSteps(null);
            return;
        }
        setStepsLoading(true);
        const fetchSteps = async () => {
            const enterpriseProvider = EnterpriseProvider.getInstance();
            try {
                const result = await enterpriseProvider.getEnterpriseConstructionStepNew(selectedEnterprise);
                // Converter ConstructionStepNew[] para ConstructionStep[]
                const convertedSteps = result.map((step, index) => {
                    const constructionStep = new ConstructionStep(
                        step.id?.toString() || `step-${index}`, // id
                        step.name || '', // name
                        step.comment || '', // description
                        0, // estimationDuration
                        '', // durationType
                        new ConstructionStatus(step.status), // status
                        [] as Step[] // steps
                    );

                    // Adicionar campos extras para compatibilidade com o componente
                    (constructionStep as any).progress = step.progress;
                    (constructionStep as any).startDate = step.startDate;
                    (constructionStep as any).endDate = step.endDate;
                    (constructionStep as any).observations = step.comment;

                    // Mapear imagens se existirem no resultado da API
                    if ((step as any).images && Array.isArray((step as any).images)) {
                        (constructionStep as any).images = (step as any).images;
                    } else {
                        (constructionStep as any).images = [];
                    }

                    // Mapear histórico se existir no resultado da API
                    if ((step as any).histories && Array.isArray((step as any).histories)) {
                        (constructionStep as any).history = (step as any).histories.map((historyItem: any) => {
                            // Determinar o tipo de ação baseado no título ou ação
                            let actionType = historyItem.action || 'updated';
                            let description = historyItem.title || 'Etapa atualizada';

                            // Melhorar a detecção do tipo de ação
                            if (historyItem.action === 'created') {
                                actionType = 'created';
                                description = 'Etapa criada';
                            } else if (historyItem.title?.toLowerCase().includes('status')) {
                                actionType = 'status_changed';
                                description = historyItem.title || 'Status da etapa alterado';
                            } else if (historyItem.title?.toLowerCase().includes('progresso') || historyItem.title?.toLowerCase().includes('progress')) {
                                actionType = 'progress_updated';
                                description = historyItem.title || 'Progresso da etapa atualizado';
                            } else if (historyItem.title?.toLowerCase().includes('imagem') || historyItem.title?.toLowerCase().includes('foto')) {
                                if (historyItem.title?.toLowerCase().includes('removida') || historyItem.title?.toLowerCase().includes('excluída')) {
                                    actionType = 'image_removed';
                                    description = historyItem.title || 'Imagem removida da etapa';
                                } else {
                                    actionType = 'image_added';
                                    description = historyItem.title || 'Imagem adicionada à etapa';
                                }
                            } else if (historyItem.title?.toLowerCase().includes('data') || historyItem.title?.toLowerCase().includes('data')) {
                                actionType = 'date_updated';
                                description = historyItem.title || 'Data da etapa atualizada';
                            }

                            return {
                                id: historyItem.id?.toString() || Date.now().toString(),
                                action: actionType,
                                description: description,
                                timestamp: historyItem.createdAt ? new Date(historyItem.createdAt * 1000).toISOString() : new Date().toISOString(),
                                user: historyItem.user?.email || 'Usuário',
                                changes: []
                            };
                        });
                    } else {
                        (constructionStep as any).history = [];
                    }

                    return constructionStep;
                });
                setSteps(convertedSteps);
            } catch (e) {
                setSteps([]);
            }
            setStepsLoading(false);
        };
        fetchSteps();
    }, [selectedEnterprise]);

    React.useEffect(() => {
        if (isDialogOpen && editingStage) {
            setStepName(editingStage.name || '');
            setStepProgress((editingStage as any).progress || 0);
            setStepStatus(editingStage.status?.getStatus?.() || 'pending');

            // Converter timestamp para formato de data se necessário
            const startDate = (editingStage as any).startDate;
            if (startDate) {
                // Se for um timestamp (número), converter para formato de data
                if (typeof startDate === 'number') {
                    const date = new Date(startDate * 1000); // Converter de segundos para milissegundos
                    setStepStartDate(date.toISOString().split('T')[0]);
                } else {
                    setStepStartDate(startDate);
                }
            } else {
                setStepStartDate('');
            }

            const endDate = (editingStage as any).endDate;
            if (endDate) {
                // Se for um timestamp (número), converter para formato de data
                if (typeof endDate === 'number') {
                    const date = new Date(endDate * 1000); // Converter de segundos para milissegundos
                    setStepEndDate(date.toISOString().split('T')[0]);
                } else {
                    setStepEndDate(endDate);
                }
            } else {
                setStepEndDate('');
            }

            setStepObs((editingStage as any).observations || '');
            setStepImages([]);
            setSelectedFiles([]);
        }
    }, [isDialogOpen, editingStage]);

    return (
        <div className="construction-steps-container">
            <div className="select-enterprise">
                <label className="select-label-docs">
                    Selecione um empreendimento para visualizar as etapas:
                </label>
                <div className='teste'>
                    <select
                        id="enterprise-select-steps"
                        className="enterprise-select-steps"
                        value={selectedEnterprise}
                        onChange={handleEnterpriseChange}
                        disabled={loading}
                    >
                        <option value="" disabled>Selecione um empreendimento</option>
                        {enterprises.map((enterprise) => (
                            <option key={enterprise.id} value={enterprise.id}>
                                {enterprise.name}
                            </option>
                        ))}
                    </select>
                    {selectedEnterprise && (
                        <button
                            className="add-button-etapa"
                            onClick={handleOpenCreateModal}>
                            + Nova etapa
                        </button>
                    )}
                </div>
            </div>
            {!selectedEnterprise && (
                <div className="empty-state-construction">
                    <div className="empty-card">
                        <MapPinHouse size={150} className="icon" />
                        <div className="empty-text-docs">Selecione um empreendimento para visualizar os resultados.</div>
                    </div>
                </div>
            )}
            {selectedEnterprise && !stepsLoading && steps && steps.length === 0 && (
                <div className="empty-state-construction">
                    <div className="empty-card">
                        <Hammer size={64} className="icon" />
                        <h2>Nenhuma etapa criada</h2>
                        <div className="desc">Comece adicionando a primeira etapa da sua obra</div>
                        <button className="add-button-etapa" onClick={handleOpenCreateModal}>
                            + Criar primeira etapa
                        </button>
                    </div>
                </div>
            )}
            {selectedEnterprise && !stepsLoading && steps && steps.length > 0 && (
                <>
                    <div className="tabs-navigation">
                        <button
                            className={`tab-button ${activeTab === "visao" ? "active" : ""}`}
                            onClick={() => setActiveTab("visao")}
                        >
                            Visão geral
                        </button>
                        <button
                            className={`tab-button ${activeTab === "etapas" ? "active" : ""}`}
                            onClick={() => setActiveTab("etapas")}
                        >
                            Etapas
                        </button>
                        <button
                            className={`tab-button ${activeTab === "galeria" ? "active" : ""}`}
                            onClick={() => setActiveTab("galeria")}
                        >
                            Galeria
                        </button>
                        <button
                            className={`tab-button ${activeTab === "historico" ? "active" : ""}`}
                            onClick={() => setActiveTab("historico")}
                        >
                            Histórico
                        </button>
                    </div>

                    {activeTab === "visao" && (
                        (() => {
                            const { concluidas, andamento, naoIniciadas } = getEtapasByStatus(steps);
                            const progressoGeral = getProgressoGeral(steps);
                            const totalFotos = getTotalFotos(steps);
                            return (
                                <div className="overview-tabs">
                                    {/* Estatísticas */}
                                    <div className="stats-grid">
                                        <div className="card">
                                            <div className="card-header">
                                                <h3 className="card-title">Progresso geral</h3>
                                            </div>
                                            <div className="card-content">
                                                <div className="progress-summary">
                                                    <span className="progress-value">{progressoGeral}%</span>
                                                    <div className="progress-circle">
                                                        <div
                                                            className="progress-fill"
                                                            style={{ clipPath: `polygon(0 0, 100% 0, 100% ${progressoGeral}%, 0 ${progressoGeral}%)` }}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="card">
                                            <div className="card-header">
                                                <h3 className="card-title">Etapas</h3>
                                            </div>
                                            <div className="card-content">
                                                <div className="stage-info">
                                                    <span className="stage-count">{steps.length}</span>
                                                    <span className="badge badge-success">{concluidas.length} concluídas</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="card">
                                            <div className="card-header">
                                                <h3 className="card-title">Em andamento</h3>
                                            </div>
                                            <div className="card-content">
                                                <div className="stage-info">
                                                    <span className="stage-count text-blue">{andamento.length}</span>
                                                    <Clock className="icon blue" />
                                                </div>
                                            </div>
                                        </div>
                                        <div className="card">
                                            <div className="card-header">
                                                <h3 className="card-title">Fotos</h3>
                                            </div>
                                            <div className="card-content">
                                                <div className="stage-info">
                                                    <span className="stage-count">{totalFotos}</span>
                                                    <Camera className="icon gray" />
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Progresso por Etapa */}
                                    <div className="progress-chart">
                                        <div className="progress-chart-header">
                                            <div className="progress-chart-title">Progresso por etapa</div>
                                            <div className="progress-chart-description">Visualização do andamento de cada etapa da obra</div>
                                        </div>
                                        <div className="progress-chart-content">
                                            <div className="stage-list">
                                                {steps.map((stage) => (
                                                    <div key={stage.id} className="stage-item">
                                                        <div className="stage-header">
                                                            <div className="stage-info">
                                                                <div className="etapa-icone">
                                                                    {getIconById(stage.id)}
                                                                </div>
                                                                <span className="stage-name">{stage.name}</span>
                                                                <Tooltip text="Histórico">
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleViewHistory(stage)}
                                                                        className="history-button"
                                                                    >
                                                                        <History className="icon" />
                                                                    </button>
                                                                </Tooltip>
                                                            </div>
                                                            <span className="stage-percentage">{(stage as any).progress || 0}%</span>
                                                        </div>
                                                        <div className="progress-bar-background">
                                                            <div
                                                                className={`progress-bar-fill ${stage.status?.getStatus?.() === "pending" ? "not-started" : stage.status?.getStatus?.()}`}
                                                                style={{ width: `${(stage as any).progress || 0}%` }}
                                                            />
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Grid de status das etapas */}
                                    <div className="stage-status-grid" style={{ marginTop: '2rem' }}>

                                        {(() => {
                                            const renderList = (
                                                arr: ConstructionStep[] | undefined,
                                                emptyMsg: string,
                                                getProgress: (s: ConstructionStep) => string,
                                                statusClass: string
                                            ) => {
                                                if (!arr || arr.length === 0) {
                                                    return (
                                                        <li className="status-item status-empty">{emptyMsg}</li>
                                                    );
                                                }
                                                return arr.map((s: ConstructionStep, idx: number) => (
                                                    <li key={s.id || idx} className="status-item">
                                                        <div className="status-content">
                                                            <span className="stage-name">{s.name}</span>
                                                            <span className={`status-badge ${statusClass}`}>{getProgress(s)}</span>
                                                        </div>
                                                    </li>
                                                ));
                                            };
                                            return (
                                                <>
                                                    <div className="status-card">
                                                        <div className="status-header completed">
                                                            <div className="status-title">
                                                                <CheckCircle2 className="status-icon completed" />
                                                                <span className="status-title-text">Concluídas</span>
                                                            </div>
                                                        </div>
                                                        <div className="status-content p-0">
                                                            <ul className="status-list">
                                                                {renderList(concluidas, "Nenhuma etapa concluída", () => "100%", "completed")}
                                                            </ul>
                                                        </div>
                                                    </div>
                                                    <div className="status-card">
                                                        <div className="status-header in-progress">
                                                            <div className="status-title">
                                                                <Clock className="status-icon in-progress" />
                                                                <span className="status-title-text">Em andamento</span>
                                                            </div>
                                                        </div>
                                                        <div className="status-content p-0">
                                                            <ul className="status-list">
                                                                {renderList(andamento, "Nenhuma etapa em andamento", (s: ConstructionStep) => `${(s as any).progress || 0}%`, "in-progress")}
                                                            </ul>
                                                        </div>
                                                    </div>
                                                    <div className="status-card">
                                                        <div className="status-header not-started">
                                                            <div className="status-title">
                                                                <AlertCircle className="status-icon not-started" />
                                                                <span className="status-title-text">Não iniciadas</span>
                                                            </div>
                                                        </div>
                                                        <div className="status-content p-0">
                                                            <ul className="status-list">
                                                                {renderList(naoIniciadas, "Todas as etapas foram iniciadas", () => "0%", "not-started")}
                                                            </ul>
                                                        </div>
                                                    </div>
                                                </>
                                            );
                                        })()}
                                    </div>
                                </div>
                            );
                        })()
                    )}

                    {activeTab === "etapas" && (
                        <div className="etapas-content">
                            {/* Filtros */}
                            <div className="filtros-card">
                                <div className="filtros-conteudo">
                                    <div className="filtros-wrapper">
                                        <div className="filtro-busca">
                                            <Search className="icone-busca" />
                                            <input
                                                type="text"
                                                placeholder="Buscar etapas..."
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                                className="input-busca"
                                            />
                                        </div>

                                        <select
                                            value={statusFilter}
                                            onChange={(e) => setStatusFilter(e.target.value)}
                                            className="select-trigger"
                                        >
                                            <option value="all">Todos os status</option>
                                            <option value="not-started">Não iniciadas</option>
                                            <option value="in-progress">Em andamento</option>
                                            <option value="completed">Concluídas</option>
                                        </select>

                                        <select
                                            value={sortBy}
                                            onChange={(e) => setSortBy(e.target.value)}
                                            className="select-trigger"
                                        >
                                            <option value="name">Nome</option>
                                            <option value="progress">Progresso</option>
                                            <option value="date">Data</option>
                                        </select>

                                        <div className="contador-etapas">
                                            {filteredStages.length} de {stages.length} etapas
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Lista de Etapas */}
                            <div className="lista-etapas">
                                {filteredStages.map((stage) => (
                                    <div
                                        key={stage.id}
                                        className={`etapa-card ${selectedStage === stage.id ? "selecionada" : ""}`}
                                    >
                                        <div className={`etapa-header status-${stage.status?.getStatus?.() || 'pending'}`}>
                                            <div className="etapa-topo">
                                                <div className="etapa-titulo">
                                                    <div className="etapa-icone">
                                                        {getIconById(stage.id)}
                                                    </div>
                                                    <h3>{stage.name}</h3>
                                                </div>

                                                <div className="dropdown-menu">
                                                    <button className="btn-menu">
                                                        <EllipsisVertical className="icone-menu" />
                                                    </button>
                                                    <div className="dropdown-content">
                                                        <button
                                                            className="dropdown-item"
                                                            onClick={() => { setEditingStage(stage); setIsDialogOpen(true); }}
                                                        >
                                                            <Edit className="icone-menu-opcao" />
                                                            Editar
                                                        </button>
                                                        <button
                                                            className="dropdown-item"
                                                            onClick={() => handleViewHistory(stage)}
                                                        >
                                                            <History className="icone-menu-opcao" />
                                                            Ver histórico
                                                        </button>
                                                        <button
                                                            className="dropdown-item detalhes"
                                                            onClick={() => handleShowDetails(stage)}
                                                        >
                                                            <Search className="icone-menu-opcao" />
                                                            Detalhes
                                                        </button>
                                                        <button
                                                            className="dropdown-item excluir"
                                                            onClick={() => handleDeleteStage(stage.id)}
                                                        >
                                                            <Trash2 className="icone-menu-opcao" />
                                                            Excluir
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="etapa-conteudo">
                                            <div className="etapa-progresso">
                                                <div className="etapa-progresso-topo">
                                                    <span>Progresso</span>
                                                    <span>{(stage as any).progress || 0}%</span>
                                                </div>
                                                <div className="etapa-barra">
                                                    <div
                                                        className={`etapa-barra-preenchida status-${stage.status?.getStatus?.() || 'pending'}`}
                                                        style={{ width: `${(stage as any).progress || 0}%` }}
                                                    />
                                                </div>
                                            </div>

                                            <div className="etapa-data">
                                                <Calendar className="icone-info" />
                                                <span>
                                                    {formatDate((stage as any).startDate)}
                                                </span>
                                            </div>

                                            <div className="etapa-infos-extra">
                                                <div className='etapa-infos-extra-item'>
                                                    <Camera className="icone-info" />
                                                    <span>{(stage as any).images?.length || 0} foto{(stage as any).images?.length !== 1 ? "s" : ""}</span>
                                                </div>
                                                <div className='etapa-infos-extra-item-2'>
                                                    <History className="icone-info" />
                                                    <span>{(stage as any).history?.length || 0} alteraç{(stage as any).history?.length !== 1 ? "ões" : "ão"}</span>
                                                </div >
                                            </div >

                                            <div className={`etapa-status status-${stage.status?.getStatus?.() || 'pending'}`}>
                                                {getStatusText(stage.status)}
                                            </div>
                                        </div >

                                        {/* <div className="etapa-footer">
                                            <button
                                                className="btn-footer"
                                                onClick={() => setSelectedStage(selectedStage === stage.id ? null : stage.id)}
                                            >
                                                <Eye className="icone-footer" /> Detalhes
                                            </button>
                                            <button
                                                className="btn-footer btn-historico"
                                                onClick={() => handleViewHistory(stage)}
                                            >
                                                <History className="icone-footer" /> Histórico
                                            </button>
                                            <button
                                                className="btn-footer btn-editar"
                                                onClick={() => {
                                                    setEditingStage(stage)
                                                    setIsDialogOpen(true)
                                                }}
                                            >
                                                <Edit className="icone-editar" />
                                                Editar Etapa
                                            </button>
                                        </div> */}

                                        {
                                            selectedStage === stage.id && (
                                                <div className="etapa-detalhes">
                                                    {(stage as any).observations && (
                                                        <div className="observacoes">
                                                            <h4>Observações</h4>
                                                            <p>{(stage as any).observations || "TESTE OBSERVAÇÃO"}</p>
                                                        </div>
                                                    )}

                                                    {(stage as any).images && (stage as any).images.length > 0 && (
                                                        <div className="fotos">
                                                            <h4>Fotos</h4>
                                                            <div className="fotos-grid">
                                                                {(stage as any).images.slice(0, 6).map((photo: any, index: number) => (
                                                                    <div key={index} className="foto">
                                                                        <img
                                                                            src={photo.url || "/placeholder.svg"}
                                                                            alt={photo.description || "Foto da etapa"}
                                                                            className="foto-img"
                                                                        />
                                                                        <button
                                                                            className="delete-image-btn"
                                                                            onClick={() => handleDeleteImage(stage.id, photo.id)}
                                                                            title="Excluir imagem"
                                                                        >
                                                                            <Trash2 size={16} />
                                                                        </button>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                            {(stage as any).images.length > 6 && (
                                                                <p className="mais-fotos">+{(stage as any).images.length - 6} foto(s)</p>
                                                            )}
                                                        </div>
                                                    )}
                                                    <div className="editar-etapa">
                                                    </div>
                                                </div>
                                            )
                                        }
                                    </div >
                                ))}
                            </div >
                        </div >
                    )}
                    {
                        activeTab === "galeria" && (
                            <div className="galeria-fotos">
                                <div className="galeria-card">
                                    <div className="galeria-header">
                                        <h2>Galeria de fotos</h2>
                                        <p>Todas as fotos do empreendimento organizadas por etapa</p>
                                    </div>

                                    <div className="galeria-conteudo">
                                        {stages.filter((stage) => (stage as any).images?.length > 0).length === 0 ? (
                                            <div className="galeria-vazia">
                                                <Camera className="galeria-vazia-icone" />
                                                <h3>Nenhuma foto adicionada</h3>
                                                <p>Adicione fotos às etapas para visualizá-las aqui</p>
                                            </div>
                                        ) : (
                                            <div className="galeria-lista">
                                                {stages
                                                    .filter((stage) => (stage as any).images?.length > 0)
                                                    .map((stage) => (
                                                        <div key={stage.id} className="galeria-etapa">
                                                            <div className="galeria-etapa-header">
                                                                {getStatusIcon(stage.status)}
                                                                <h3>{stage.name}</h3>
                                                                <span className={`etapa-badge status-${stage.status?.getStatus?.() || 'pending'}`}>
                                                                    {(stage as any).images?.length || 0} foto{(stage as any).images?.length !== 1 ? "s" : ""}
                                                                </span>
                                                            </div>

                                                            <div className="galeria-grade">
                                                                {(stage as any).images?.map((photo: any, index: number) => (
                                                                    <div key={index} className="foto-container">
                                                                        <img
                                                                            src={photo.url || "/placeholder.svg"}
                                                                            alt={photo.description || "Foto da etapa"}
                                                                            className="foto-img"
                                                                        />
                                                                        <div className="foto-overlay">
                                                                            <span className="foto-descricao">{photo.description || "Foto da etapa"}</span>
                                                                        </div>
                                                                        <button
                                                                            className="delete-image-btn"
                                                                            onClick={() => handleDeleteImage(stage.id, photo.id)}
                                                                            title="Excluir imagem"
                                                                        >
                                                                            <Trash2 size={16} />
                                                                        </button>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )
                    }
                    {
                        activeTab === "historico" && (
                            <div className="historico-geral">
                                <div className="historico-card">
                                    <div className="historico-header">
                                        <h2>Histórico de alterações</h2>
                                        <p>Todas as mudanças realizadas nas etapas do empreendimento</p>
                                    </div>

                                    <div className="historico-conteudo">
                                        <div className="scroll-area">
                                            {stages
                                                .flatMap((stage) => (stage as any).history?.map((entry: any) => ({
                                                    ...entry,
                                                    stageName: stage.name,
                                                    stageId: stage.id,
                                                })) || [])
                                                .length === 0 ? (
                                                <div className="historico-vazio">
                                                    <History className="icone-vazio" />
                                                    <h3>Nenhum histórico disponível</h3>
                                                    <p>As alterações nas etapas aparecerão aqui</p>
                                                </div>
                                            ) : (
                                                <div className="historico-lista">
                                                    {stages
                                                        .flatMap((stage) =>
                                                            (stage as any).history?.map((entry: any) => ({
                                                                ...entry,
                                                                stageName: stage.name,
                                                                stageId: stage.id,
                                                            })) || []
                                                        )
                                                        .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
                                                        .map((entry) => (
                                                            <div
                                                                key={`${entry.stageId}-${entry.id}`}
                                                                className={`historico-item ${getActionColor(entry.action)}`}
                                                            >
                                                                <div className="historico-item-wrapper">
                                                                    <div className="historico-icone">{getActionIcon(entry.action)}</div>
                                                                    <div className="historico-detalhes">
                                                                        <div className="historico-topo">
                                                                            <h4>{entry.stageName}</h4>
                                                                            <div className="historico-info">
                                                                                <span className={`action-type action-${entry.action}`}>{getActionText(entry.action)}</span>
                                                                                <span className="action-date"> feita em: {formatDate(entry.timestamp)}</span>
                                                                            </div>
                                                                        </div>
                                                                        <p className="descricao">{entry.description}</p>
                                                                        {/* <div className="usuario">
                                                                        <User className="icone-usuario" />
                                                                        <span>{entry.user}</span>
                                                                    </div> */}

                                                                        {entry.changes && entry.changes.length > 0 && (
                                                                            <div className="mudancas">
                                                                                {entry.changes.map((change: any, index: number) => (
                                                                                    <div key={index} className="campo-alterado">
                                                                                        <span className="campo">{change.field}:</span>
                                                                                        {change.oldValue !== null && (
                                                                                            <span className="valor-antigo">{String(change.oldValue)}</span>
                                                                                        )}
                                                                                        {change.oldValue !== null && change.newValue !== null && (
                                                                                            <ArrowRight className="icone-arrow" />
                                                                                        )}
                                                                                        {change.newValue !== null && (
                                                                                            <span className="valor-novo">{String(change.newValue)}</span>
                                                                                        )}
                                                                                    </div>
                                                                                ))}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div >
                        )
                    }
                </>
            )}
            {
                showCreateStepModal && (
                    <div className="modal-overlay-unidade">
                        <div className="modal-unidade">
                            <div className="modal-header-unidade">
                                <h2>Criar nova etapa</h2>
                                <button className="close-button-unidade" onClick={() => {
                                    setShowCreateStepModal(false);
                                    clearFormFields();
                                }}><X size={20} /></button>
                            </div>
                            <p>Adicione uma nova etapa à construção</p>
                            <div className="divisor-enterprise"></div>
                            <form className="modal-form-etapa" onSubmit={handleCreateStep}>
                                <div className="form-group">
                                    <label>Nome da etapa</label>
                                    <input type="text" placeholder="Ex: Fundação, Estrutura, Acabamento..." required value={stepName} onChange={e => setStepName(e.target.value)} />
                                </div>
                                <div className="form-row-inline">
                                    <div className="form-group" style={{ flex: 1, marginRight: '8px' }}>
                                        <label>Progresso (%)</label>
                                        <input type="number" min="0" max="100" value={stepProgress} onChange={e => setStepProgress(Number(e.target.value))} />
                                    </div>
                                    <div className="form-group" style={{ flex: 2 }}>
                                        <label>Status</label>
                                        <select value={stepStatus} onChange={e => setStepStatus(e.target.value)}>
                                            <option value="pending">Não iniciada</option>
                                            <option value="in_progress">Em andamento</option>
                                            <option value="finished">Concluída</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="form-row-inline">
                                    <div className="form-group" style={{ flex: 1, marginRight: '8px' }}>
                                        <label>Data de início</label>
                                        <input type="date" value={stepStartDate} onChange={e => setStepStartDate(e.target.value)} />
                                    </div>
                                    <div className="form-group" style={{ flex: 1 }}>
                                        <label>Data de conclusão</label>
                                        <input type="date" value={stepEndDate} onChange={e => setStepEndDate(e.target.value)} />
                                    </div>
                                </div>
                                <div className="form-group">
                                    <label>Observações</label>
                                    <textarea placeholder="Adicione observações sobre esta etapa..." value={stepObs} onChange={e => setStepObs(e.target.value)}></textarea>
                                </div>
                                <div className="form-group">
                                    <label>Fotos da etapa</label>
                                    <div className="upload-area-etapa" onClick={handleUploadBoxClick} style={{ cursor: 'pointer' }}>
                                        <Upload size={24} />
                                        <p>Clique para adicionar fotos ou arraste aqui<br />PNG, JPG, JPEG até 10MB cada</p>
                                        <input
                                            type="file"
                                            ref={fileInputRef}
                                            style={{ display: 'none' }}
                                            accept="image/png, image/jpeg, image/jpg"
                                            multiple
                                            onChange={e => {
                                                handleFileChange(e);
                                                setStepImages(e.target.files ? Array.from(e.target.files) : []);
                                            }}
                                        />
                                        {selectedFiles.length > 0 && (
                                            <ul style={{ marginTop: 8, textAlign: 'left', fontSize: 13 }}>
                                                {selectedFiles.map((file, idx) => (
                                                    <li key={idx}>{file.name}</li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                </div>
                                <div className="form-buttons-unidade">
                                    <button type="button" className="cancel-button-unidade" onClick={() => {
                                        setShowCreateStepModal(false);
                                        clearFormFields();
                                    }} disabled={isCreating}>
                                        Cancelar
                                    </button>
                                    <button type="submit" className="create-button-unidade" disabled={isCreating}>
                                        {isCreating ? 'Criando...' : 'Criar etapa'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )
            }
            {/* MODAL DE EDIÇÃO MOCKADO */}
            {
                isDialogOpen && editingStage && (
                    <div className="modal-overlay-unidade">
                        <div className="modal-unidade">
                            <div className="modal-header-unidade">
                                <h2>Editar etapa</h2>
                                <button className="close-button-unidade" onClick={() => {
                                    setIsDialogOpen(false);
                                    clearFormFields();
                                }} disabled={isUpdating}><X size={20} /></button>
                            </div>
                            <p>Edite as informações da etapa</p>
                            <div className="divisor-enterprise"></div>
                            <form className="modal-form-etapa" onSubmit={async (e) => {
                                e.preventDefault();

                                if (isUpdating) return; // Prevenir múltiplos submits

                                setIsUpdating(true);

                                try {
                                    // 1. Fazer upload das novas imagens primeiro
                                    const uploadImageProvider = UploadImageProvider.getInstance();
                                    const uploadedImageUrls: string[] = [];

                                    if (stepImages.length > 0) {
                                        for (const image of stepImages) {
                                            try {
                                                const imageUrl = await uploadImageProvider.upload(image);
                                                uploadedImageUrls.push(imageUrl);
                                            } catch (error) {
                                                console.error('Erro ao fazer upload da imagem:', error);
                                                toast.error('Erro ao fazer upload de uma das imagens. Tente novamente.');
                                                return;
                                            }
                                        }
                                    }

                                    // 2. Converter stepStartDate e stepEndDate para timestamp em segundos (não milissegundos)
                                    const startDateTimestamp = stepStartDate ? Math.floor(new Date(stepStartDate).getTime() / 1000).toString() : '';
                                    const endDateTimestamp = stepEndDate ? Math.floor(new Date(stepEndDate).getTime() / 1000).toString() : '';

                                    // 3. Criar objeto ConstructionStepNew para enviar ao backend
                                    const updatedStep = new ConstructionStepNew(
                                        editingStage.id ? parseInt(editingStage.id) : undefined, // id
                                        stepName, // name
                                        stepName, // title (usando o mesmo valor do name)
                                        stepStatus, // status
                                        stepObs, // comment
                                        stepProgress, // progress
                                        startDateTimestamp || undefined, // startDate
                                        endDateTimestamp || null // endDate
                                    );

                                    // 4. Chamar o método de atualização do provider
                                    await enterpriseProvider.updateEnterpriseConstructionStepNew(updatedStep);

                                    // 5. Adicionar as novas imagens ao step atualizado
                                    if (uploadedImageUrls.length > 0 && editingStage.id) {
                                        for (const imageUrl of uploadedImageUrls) {
                                            try {
                                                await enterpriseProvider.createEnterpriseConstructionStepImageNew(
                                                    editingStage.id,
                                                    imageUrl
                                                );
                                            } catch (error) {
                                                console.error('Erro ao adicionar imagem ao step:', error);
                                                // Não interromper o processo se falhar ao adicionar imagem
                                            }
                                        }
                                    }

                                    // 6. Recarregar os dados do backend para garantir sincronização
                                    try {
                                        const result = await enterpriseProvider.getEnterpriseConstructionStepNew(selectedEnterprise);
                                        // Converter ConstructionStepNew[] para ConstructionStep[]
                                        const convertedSteps = result.map((step, index) => {
                                            const constructionStep = new ConstructionStep(
                                                step.id?.toString() || `step-${index}`, // id
                                                step.name || '', // name
                                                step.comment || '', // description
                                                0, // estimationDuration
                                                '', // durationType
                                                new ConstructionStatus(step.status), // status
                                                [] as Step[] // steps
                                            );

                                            // Adicionar campos extras para compatibilidade com o componente
                                            (constructionStep as any).progress = step.progress;
                                            (constructionStep as any).startDate = step.startDate;
                                            (constructionStep as any).endDate = step.endDate;
                                            (constructionStep as any).observations = step.comment;

                                            // Mapear imagens se existirem no resultado da API
                                            if ((step as any).images && Array.isArray((step as any).images)) {
                                                (constructionStep as any).images = (step as any).images;
                                            } else {
                                                (constructionStep as any).images = [];
                                            }

                                            // Mapear histórico se existir no resultado da API
                                            if ((step as any).histories && Array.isArray((step as any).histories)) {
                                                (constructionStep as any).history = (step as any).histories.map((historyItem: any) => {
                                                    // Determinar o tipo de ação baseado no título ou ação
                                                    let actionType = historyItem.action || 'updated';
                                                    let description = historyItem.title || 'Etapa atualizada';

                                                    // Melhorar a detecção do tipo de ação
                                                    if (historyItem.action === 'created') {
                                                        actionType = 'created';
                                                        description = 'Etapa criada';
                                                    } else if (historyItem.title?.toLowerCase().includes('status')) {
                                                        actionType = 'status_changed';
                                                        description = historyItem.title || 'Status da etapa alterado';
                                                    } else if (historyItem.title?.toLowerCase().includes('progresso') || historyItem.title?.toLowerCase().includes('progress')) {
                                                        actionType = 'progress_updated';
                                                        description = historyItem.title || 'Progresso da etapa atualizado';
                                                    } else if (historyItem.title?.toLowerCase().includes('imagem') || historyItem.title?.toLowerCase().includes('foto')) {
                                                        if (historyItem.title?.toLowerCase().includes('removida') || historyItem.title?.toLowerCase().includes('excluída')) {
                                                            actionType = 'image_removed';
                                                            description = historyItem.title || 'Imagem removida da etapa';
                                                        } else {
                                                            actionType = 'image_added';
                                                            description = historyItem.title || 'Imagem adicionada à etapa';
                                                        }
                                                    } else if (historyItem.title?.toLowerCase().includes('data') || historyItem.title?.toLowerCase().includes('data')) {
                                                        actionType = 'date_updated';
                                                        description = historyItem.title || 'Data da etapa atualizada';
                                                    }

                                                    return {
                                                        id: historyItem.id?.toString() || Date.now().toString(),
                                                        action: actionType,
                                                        description: description,
                                                        timestamp: historyItem.createdAt ? new Date(historyItem.createdAt * 1000).toISOString() : new Date().toISOString(),
                                                        user: historyItem.user?.email || 'Usuário',
                                                        changes: []
                                                    };
                                                });
                                            } else {
                                                (constructionStep as any).history = [];
                                            }

                                            return constructionStep;
                                        });
                                        setSteps(convertedSteps);
                                    } catch (error) {
                                        console.error('Erro ao recarregar etapas após edição:', error);
                                        // Em caso de erro, ainda atualiza localmente para não perder a edição
                                        setSteps(prev => prev ? prev.map(s => {
                                            if (s.id === editingStage.id) {
                                                const updated = {
                                                    ...s,
                                                    name: stepName,
                                                    progress: stepProgress,
                                                    status: new ConstructionStatus(stepStatus),
                                                    startDate: stepStartDate,
                                                    endDate: stepEndDate,
                                                    observations: stepObs,
                                                };
                                                return updated;
                                            }
                                            return s;
                                        }) : []);
                                    }

                                    // 7. Fechar modal e limpar estados
                                    setIsDialogOpen(false);
                                    setEditingStage(null);
                                    clearFormFields();
                                    toast.success('Etapa atualizada com sucesso!');

                                } catch (error) {
                                    console.error('Erro ao atualizar etapa:', error);
                                    toast.error('Erro ao atualizar etapa. Tente novamente.');
                                } finally {
                                    setIsUpdating(false);
                                }
                            }}>
                                <div className="form-group">
                                    <label>Nome da etapa</label>
                                    <input type="text" required value={stepName} onChange={e => setStepName(e.target.value)} />
                                </div>
                                <div className="form-row-inline">
                                    <div className="form-group" style={{ flex: 1, marginRight: '8px' }}>
                                        <label>Progresso (%)</label>
                                        <input type="number" min="0" max="100" value={stepProgress} onChange={e => setStepProgress(Number(e.target.value))} />
                                    </div>
                                    <div className="form-group" style={{ flex: 2 }}>
                                        <label>Status</label>
                                        <select value={stepStatus} onChange={e => setStepStatus(e.target.value)}>
                                            <option value="pending">Não iniciada</option>
                                            <option value="in_progress">Em andamento</option>
                                            <option value="finished">Concluída</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="form-row-inline">
                                    <div className="form-group" style={{ flex: 1, marginRight: '8px' }}>
                                        <label>Data de início</label>
                                        <input type="date" value={stepStartDate} onChange={e => setStepStartDate(e.target.value)} />
                                    </div>
                                    <div className="form-group" style={{ flex: 1 }}>
                                        <label>Data de conclusão</label>
                                        <input type="date" value={stepEndDate} onChange={e => setStepEndDate(e.target.value)} />
                                    </div>
                                </div>
                                <div className="form-group">
                                    <label>Observações</label>
                                    <textarea value={stepObs} onChange={e => setStepObs(e.target.value)}></textarea>
                                </div>
                                <div className="form-group">
                                    <label>Fotos da etapa</label>
                                    <div className="upload-area-etapa" onClick={handleUploadBoxClick} style={{ cursor: 'pointer' }}>
                                        <Upload size={24} />
                                        <p>Clique para adicionar fotos ou arraste aqui<br />PNG, JPG, JPEG até 10MB cada</p>
                                        <input
                                            type="file"
                                            ref={fileInputRef}
                                            style={{ display: 'none' }}
                                            accept="image/png, image/jpeg, image/jpg"
                                            multiple
                                            onChange={e => {
                                                handleFileChange(e);
                                                setStepImages(e.target.files ? Array.from(e.target.files) : []);
                                            }}
                                        />
                                        {selectedFiles.length > 0 && (
                                            <ul style={{ marginTop: 8, textAlign: 'left', fontSize: 13 }}>
                                                {selectedFiles.map((file, idx) => (
                                                    <li key={idx}>{file.name}</li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                </div>
                                <div className="form-buttons-unidade">
                                    <button type="button" className="cancel-button-unidade" onClick={() => {
                                        setIsDialogOpen(false);
                                        clearFormFields();
                                    }} disabled={isUpdating}>
                                        Cancelar
                                    </button>
                                    <button type="submit" className="create-button-unidade" disabled={isUpdating}>
                                        {isUpdating ? 'Salvando...' : 'Salvar alterações'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )
            }
            {/* MODAL DE CONFIRMAÇÃO DE EXCLUSÃO DE IMAGEM */}
            {
                deleteImageModalOpen && (
                    <div className="modal-overlay-unidade">
                        <div className="modal-unidade">
                            <div className="modal-header-unidade">
                                <h2>Confirmar exclusão</h2>
                                <button className="close-button-unidade" onClick={cancelDeleteImage} disabled={isDeletingImage}><X size={20} /></button>
                            </div>
                            <p>Tem certeza que deseja excluir esta imagem? Esta ação não poderá ser desfeita.</p>
                            <div className="form-buttons-unidade">
                                <button type="button" className="cancel-button-unidade" onClick={cancelDeleteImage} disabled={isDeletingImage}>
                                    Cancelar
                                </button>
                                <button type="button" className="create-button-unidade" style={{ background: '#e74c3c' }} onClick={confirmDeleteImage} disabled={isDeletingImage}>
                                    {isDeletingImage ? 'Excluindo...' : 'Confirmar'}
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }
            {/* MODAL DE CONFIRMAÇÃO DE EXCLUSÃO */}
            {
                deleteModalOpen && (
                    <div className="modal-overlay-unidade">
                        <div className="modal-unidade">
                            <div className="modal-header-unidade">
                                <h2>Confirmar exclusão</h2>
                                <button className="close-button-unidade" onClick={cancelDeleteStage} disabled={isDeleting}><X size={20} /></button>
                            </div>
                            <p>Tem certeza que deseja excluir esta etapa? Esta ação não poderá ser desfeita.</p>
                            <div className="form-buttons-unidade">
                                <button type="button" className="cancel-button-unidade" onClick={cancelDeleteStage} disabled={isDeleting}>
                                    Cancelar
                                </button>
                                <button type="button" className="create-button-unidade" style={{ background: '#e74c3c' }} onClick={confirmDeleteStage} disabled={isDeleting}>
                                    {isDeleting ? 'Excluindo...' : 'Excluir'}
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }
            <ToastContainer />
        </div >
    );
};

export default ConstructionSteps; 